import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Navbar, Footer } from '@/components/Layout';
import { KutroLogo } from '@/components/Logo';
import {
  ExternalLink, Shield, Coins, Users, BarChart3, Brain,
  CheckCircle, Globe, Code, Database, Cpu, Vote, BookOpen,
  TrendingUp, Flame, Lock, Activity, Copy, Check, ArrowRight,
  Calendar, Rocket, Target, Zap, GitBranch
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const GITHUB_URL = 'https://github.com/faulknerwayne73-droid/kojiru-platform';

export default function OpenServDashboard() {
  const [stats, setStats] = useState(null);
  const [staking, setStaking] = useState(null);
  const [holders, setHolders] = useState(null);
  const [proposals, setProposals] = useState(null);
  const [copied, setCopied] = useState('');

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/token/stats`).then(r => r.json()).catch(() => null),
      fetch(`${API}/api/staking/stats`).then(r => r.json()).catch(() => null),
      fetch(`${API}/api/token/holders`).then(r => r.json()).catch(() => null),
      fetch(`${API}/api/governance/proposals`).then(r => r.json()).catch(() => null),
    ]).then(([t, s, h, p]) => {
      setStats(t); setStaking(s); setHolders(h); setProposals(p);
    });
  }, []);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const fmt = (n) => {
    if (!n) return '--';
    const v = parseFloat(n);
    if (v >= 1e9) return (v / 1e9).toFixed(1) + 'B';
    if (v >= 1e6) return (v / 1e6).toFixed(0) + 'M';
    if (v >= 1e3) return (v / 1e3).toFixed(0) + 'K';
    return v.toFixed(0);
  };

  const milestones = [
    { phase: 'Phase 1', title: 'Foundation', status: 'complete', items: ['ERC-20 Token deployed on Base Mainnet', 'Staking contract with 3-tier system', 'P2P lending marketplace (RFB/Bid)', 'User auth, consumer & lender dashboards', 'MetaMask wallet integration'] },
    { phase: 'Phase 2', title: 'Intelligence', status: 'complete', items: ['Gemini 3 Flash AI credit analysis', 'AI-powered lender bid suggestions', 'Live on-chain data dashboards', 'Staking rewards calculator', 'Holders leaderboard via Blockscout'] },
    { phase: 'Phase 3', title: 'Governance', status: 'complete', items: ['DAO proposal creation & voting', 'Token-weighted voting power', 'Research hub with 7 articles', 'Full tokenomics page with burn tracker', 'Comprehensive marketing website'] },
    { phase: 'Phase 4', title: 'Expansion', status: 'upcoming', items: ['CoinGecko & CoinMarketCap listings', 'Chainlink oracle for credit data', 'Cross-chain bridge to Ethereum', 'WalletConnect mobile support', 'Third-party security audit'] },
  ];

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-blue-500/5 rounded-full blur-[150px]" />
        <div className="container mx-auto max-w-5xl relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <div className="flex justify-center mb-6">
              <KutroLogo size={64} />
            </div>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-green-500/10 border border-green-500/20 text-sm text-green-400 mb-6">
              <CheckCircle className="w-4 h-4" />
              Live on Base Mainnet
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4" data-testid="openserv-heading">
              KOJIRU <span className="bg-gradient-to-r from-orange-500 via-blue-500 to-purple-500 bg-clip-text text-transparent">Launchpad Application</span>
            </h1>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-4">
              AI-powered peer-to-peer DeFi lending on Base. Smart contracts deployed, verified, and funded.
              Real AI credit analysis. DAO governance live. Full-stack production platform.
            </p>
            <p className="text-sm text-gray-500 max-w-xl mx-auto mb-8">
              Built with React, FastAPI, Solidity, Gemini AI, and MongoDB. Deployed on Base Mainnet (Chain 8453).
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <a href="/" data-testid="demo-link" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-blue-500 rounded-xl font-medium hover:brightness-110 transition-all">
                <Globe className="w-4 h-4" /> Live Platform
              </a>
              <a href={GITHUB_URL} target="_blank" rel="noopener noreferrer" data-testid="github-link" className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 border border-white/10 rounded-xl font-medium hover:bg-white/15 transition-all">
                <Code className="w-4 h-4" /> Source Code
              </a>
              <a href="/tokenomics" className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 border border-white/10 rounded-xl font-medium hover:bg-white/15 transition-all">
                <Coins className="w-4 h-4" /> Tokenomics
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Project Summary */}
      <section className="py-16 px-6 bg-gradient-to-b from-transparent via-blue-500/[0.03] to-transparent">
        <div className="container mx-auto max-w-5xl">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="mb-10">
            <h2 className="text-2xl font-bold text-center mb-2">Project <span className="text-blue-400">Overview</span></h2>
            <p className="text-sm text-gray-500 text-center max-w-2xl mx-auto">Executive summary for evaluators and launchpad reviewers</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-5">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="p-6 rounded-2xl glass">
              <Target className="w-6 h-6 text-orange-400 mb-3" />
              <h3 className="font-semibold text-lg mb-3">Problem</h3>
              <p className="text-sm text-gray-400 leading-relaxed">
                DeFi lending is dominated by pool-based protocols with algorithmic rates. Borrowers have no negotiating power, lenders can't differentiate, and credit assessment is non-existent. 1.7B unbanked adults have zero access to capital.
              </p>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} viewport={{ once: true }} className="p-6 rounded-2xl glass">
              <Rocket className="w-6 h-6 text-blue-400 mb-3" />
              <h3 className="font-semibold text-lg mb-3">Solution</h3>
              <p className="text-sm text-gray-400 leading-relaxed">
                KOJIRU is a P2P lending marketplace where borrowers post requests and lenders compete with bids. AI agents (Gemini 3 Flash) analyze credit risk and optimize strategies. All settlement is on-chain via verified smart contracts on Base.
              </p>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} viewport={{ once: true }} className="p-6 rounded-2xl glass">
              <Zap className="w-6 h-6 text-yellow-400 mb-3" />
              <h3 className="font-semibold text-lg mb-3">Differentiators</h3>
              <ul className="text-sm text-gray-400 space-y-2">
                <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-green-500 mt-0.5 shrink-0" /> AI-powered credit analysis (not just algorithmic rates)</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-green-500 mt-0.5 shrink-0" /> Competitive bidding model (lenders compete for borrowers)</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-green-500 mt-0.5 shrink-0" /> Deflationary token with 4 burn mechanisms</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-green-500 mt-0.5 shrink-0" /> Full DAO governance with token-weighted voting</li>
              </ul>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} viewport={{ once: true }} className="p-6 rounded-2xl glass">
              <TrendingUp className="w-6 h-6 text-green-400 mb-3" />
              <h3 className="font-semibold text-lg mb-3">Traction</h3>
              <ul className="text-sm text-gray-400 space-y-2">
                <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-green-500 mt-0.5 shrink-0" /> Token & staking contracts live on Base Mainnet</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-green-500 mt-0.5 shrink-0" /> 1M KOJIRU staking reward pool funded</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-green-500 mt-0.5 shrink-0" /> AI credit analysis & bid suggestions operational</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-green-500 mt-0.5 shrink-0" /> Full platform with 18+ pages deployed</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Live Stats */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-5xl">
          <motion.h2 initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-2xl font-bold text-center mb-8">
            Live <span className="text-blue-400">On-Chain</span> Metrics
          </motion.h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Max Supply', value: stats ? fmt(stats.max_supply) : '1B', sub: 'KOJIRU', icon: Coins, color: 'text-blue-400' },
              { label: 'Circulating', value: stats ? fmt(stats.total_supply) : '100M', sub: `${stats?.circulating_pct || 10}%`, icon: TrendingUp, color: 'text-green-400' },
              { label: 'Reward Pool', value: staking ? fmt(staking.reward_pool) : '--', sub: 'KOJIRU funded', icon: Lock, color: 'text-purple-400' },
              { label: 'Staking APY', value: staking ? `${staking.reward_rate_pct}%` : '10%', sub: 'Annual yield', icon: BarChart3, color: 'text-orange-400' },
              { label: 'Token Holders', value: holders ? holders.total_holders : '--', sub: 'On Base', icon: Users, color: 'text-cyan-400' },
              { label: 'Active Proposals', value: proposals ? proposals.proposals?.filter(p => p.status === 'active').length : '--', sub: 'DAO governance', icon: Vote, color: 'text-yellow-400' },
              { label: 'AI Model', value: 'Gemini', sub: '3 Flash', icon: Brain, color: 'text-pink-400' },
              { label: 'Blog Articles', value: '6', sub: 'Published', icon: BookOpen, color: 'text-teal-400' },
            ].map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                viewport={{ once: true }}
                data-testid={`stat-${i}`}
                className="bg-white/[0.03] rounded-xl p-4 border border-white/5"
              >
                <m.icon className={`w-5 h-5 ${m.color} mb-2`} />
                <div className={`text-2xl font-bold ${m.color}`}>{m.value}</div>
                <div className="text-xs text-gray-500">{m.label}</div>
                <div className="text-[10px] text-gray-600">{m.sub}</div>
              </motion.div>
            ))}
          </div>
          <div className="flex items-center justify-center gap-2 mt-4 text-xs text-gray-600">
            <Activity className="w-3 h-3 text-green-500" />
            <span>Live data from Base Mainnet via web3 + Blockscout</span>
          </div>
        </div>
      </section>

      {/* Verified Contracts */}
      <section className="py-16 px-6 bg-gradient-to-b from-transparent via-green-500/5 to-transparent">
        <div className="container mx-auto max-w-5xl">
          <motion.h2 initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-2xl font-bold text-center mb-8">
            Verified <span className="text-green-400">Smart Contracts</span>
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { name: 'KOJIRU Token (ERC-20)', features: ['1B max supply', 'Burn mechanism', 'Mint/burn by owner', 'OpenZeppelin standard'], verified: true },
              { name: 'KOJIRU Staking', features: ['10% APY rewards', '3-tier AI access', '7-day min lock', '1M KOJIRU funded'], verified: true },
            ].map((c, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                data-testid={`contract-${i}`}
                className="bg-white/[0.03] rounded-2xl border border-green-500/20 p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Shield className="w-6 h-6 text-green-500" />
                  <div>
                    <h3 className="font-semibold">{c.name}</h3>
                    <div className="flex items-center gap-1">
                      <CheckCircle className="w-3 h-3 text-green-500" />
                      <span className="text-xs text-green-400">Verified on Sourcify</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-black/30 rounded-lg p-2.5 mb-4">
                  <span className="text-xs text-gray-400 font-mono">Deployed on Base Mainnet</span>
                </div>
                <ul className="space-y-1 mb-4">
                  {c.features.map((f, j) => (
                    <li key={j} className="text-xs text-gray-400 flex items-center gap-2">
                      <CheckCircle className="w-3 h-3 text-green-500/50" /> {f}
                    </li>
                  ))}
                </ul>
                <p className="text-[10px] text-gray-600">Contract addresses available at official launch.</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Roadmap / Milestones */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-5xl">
          <motion.h2 initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-2xl font-bold text-center mb-8">
            Development <span className="text-purple-400">Roadmap</span>
          </motion.h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {milestones.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                data-testid={`milestone-${i}`}
                className={`rounded-2xl border p-5 ${m.status === 'complete' ? 'border-green-500/20 bg-green-500/[0.03]' : 'border-blue-500/20 bg-blue-500/[0.03]'}`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded ${m.status === 'complete' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'}`}>
                    {m.phase}
                  </span>
                  {m.status === 'complete' ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <GitBranch className="w-4 h-4 text-blue-400" />
                  )}
                </div>
                <h3 className="font-semibold mb-3">{m.title}</h3>
                <ul className="space-y-1.5">
                  {m.items.map((item, j) => (
                    <li key={j} className="text-xs text-gray-400 flex items-start gap-2">
                      <CheckCircle className={`w-3 h-3 mt-0.5 shrink-0 ${m.status === 'complete' ? 'text-green-500/60' : 'text-blue-500/40'}`} />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-16 px-6 bg-gradient-to-b from-transparent via-purple-500/[0.03] to-transparent">
        <div className="container mx-auto max-w-5xl">
          <motion.h2 initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-2xl font-bold text-center mb-8">
            Platform <span className="text-purple-400">Architecture</span>
          </motion.h2>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { title: 'Frontend', icon: Globe, color: 'from-blue-500/20', items: ['React 19 + TailwindCSS', 'Ethers.js + Web3Modal', 'Framer Motion animations', 'Shadcn/UI components'] },
              { title: 'Backend', icon: Database, color: 'from-green-500/20', items: ['FastAPI (Python)', 'MongoDB database', 'Web3.py chain reads', 'JWT authentication'] },
              { title: 'AI & Blockchain', icon: Cpu, color: 'from-purple-500/20', items: ['Gemini 3 Flash (credit AI)', 'Solidity + OpenZeppelin', 'Base L2 (EVM)', 'Hardhat deployment'] },
            ].map((stack, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className={`rounded-2xl border border-white/5 p-6 bg-gradient-to-b ${stack.color} to-transparent`}
              >
                <stack.icon className="w-8 h-8 mb-4" />
                <h3 className="font-semibold text-lg mb-3">{stack.title}</h3>
                <ul className="space-y-2">
                  {stack.items.map((item, j) => (
                    <li key={j} className="text-sm text-gray-400 flex items-center gap-2">
                      <div className="w-1 h-1 rounded-full bg-gray-500" /> {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-5xl">
          <motion.h2 initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-2xl font-bold text-center mb-8">
            Key <span className="text-orange-400">Features</span>
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { title: 'AI Credit Analysis', desc: 'Gemini 3 Flash analyzes borrower profiles and generates personalized credit insights, risk scores, and rate recommendations in real-time.', link: '/', linkText: 'Try Platform' },
              { title: 'AI Bid Suggestions', desc: 'Lenders get AI-powered bid recommendations for each loan request — suggested APR, risk assessment, and actionable advice.', link: '/', linkText: 'Try Platform' },
              { title: 'P2P Lending Marketplace', desc: 'Borrowers create loan requests, lenders compete with bids. Best rate wins. No intermediaries, no banks, no hidden fees.', link: '/auth', linkText: 'Get Started' },
              { title: 'Token Staking & Rewards', desc: '3-tier staking system (Basic/Advanced/Pro) with 10% APY. Staked tokens unlock AI agent access levels. 1M KOJIRU reward pool funded.', link: '/tokenomics', linkText: 'View Tokenomics' },
              { title: 'DAO Governance', desc: 'Token-weighted proposal creation and voting. Community-driven protocol decisions with quorum requirements.', link: '/about', linkText: 'Learn More' },
              { title: 'Interactive Tokenomics', desc: 'Live burn tracker, holders leaderboard, staking rewards calculator, DEX swap links — all reading real data from Base Mainnet.', link: '/tokenomics', linkText: 'View Tokenomics' },
            ].map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                viewport={{ once: true }}
                data-testid={`feature-${i}`}
                className="bg-white/[0.03] rounded-xl p-5 border border-white/5 hover:border-white/10 transition-colors"
              >
                <h3 className="font-semibold mb-2">{f.title}</h3>
                <p className="text-sm text-gray-400 mb-3 leading-relaxed">{f.desc}</p>
                <a href={f.link} className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
                  {f.linkText} <ArrowRight className="w-3 h-3" />
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Pages */}
      <section className="py-16 px-6 bg-gradient-to-b from-transparent via-blue-500/[0.03] to-transparent">
        <div className="container mx-auto max-w-5xl">
          <motion.h2 initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-2xl font-bold text-center mb-8">
            Platform <span className="text-cyan-400">Pages</span>
          </motion.h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { name: 'Homepage', path: '/' },
              { name: 'About', path: '/about' },
              { name: 'Idea', path: '/how-it-works' },
              { name: 'Tokenomics', path: '/tokenomics' },
              { name: 'Analytics', path: '/analytics' },
              { name: 'Research', path: '/research' },
              { name: 'Blog (6 articles)', path: '/blog' },
              { name: 'FAQ', path: '/faq' },
              { name: 'Contact', path: '/contact' },
              { name: 'Support', path: '/support' },
              { name: 'Partners', path: '/partners' },
              { name: 'Auth / Register', path: '/auth' },
              { name: 'Consumer Dashboard', path: '/consumer' },
              { name: 'Lender Dashboard', path: '/lender' },
              { name: 'AI Analysis', path: '/' },
              { name: 'Staking', path: '/' },
              { name: 'Governance', path: '/' },
              { name: 'Launchpad (this)', path: '/openserv' },
            ].map((page, i) => (
              <a key={i} href={page.path} className="bg-white/[0.03] rounded-lg p-3 border border-white/5 hover:border-blue-500/30 transition-colors text-sm text-gray-400 hover:text-white">
                {page.name}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-3xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="text-center p-10 rounded-3xl bg-gradient-to-br from-blue-500/10 via-[#080808] to-purple-500/10 border border-white/10"
          >
            <h2 className="text-3xl font-bold mb-4">Ready to Evaluate <span className="gradient-text">KOJIRU</span></h2>
            <p className="text-gray-400 mb-8 max-w-lg mx-auto">
              Deployed. Verified. AI-powered. Community-governed. Explore the live platform or review the source code.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <a href="/" className="px-8 py-3 bg-gradient-to-r from-orange-500 to-blue-500 rounded-xl font-medium hover:brightness-110 transition-all">
                Explore Platform
              </a>
              <a href="/tokenomics" className="px-8 py-3 bg-white/10 border border-white/10 rounded-xl font-medium hover:bg-white/15 transition-all">
                View Tokenomics
              </a>
              <a href={GITHUB_URL} target="_blank" rel="noopener noreferrer" className="px-8 py-3 bg-white/10 border border-white/10 rounded-xl font-medium hover:bg-white/15 transition-all">
                Source Code
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
